import React, { useState } from 'react';
import {
  Cookie,
  ShieldCheck,
  Lock,
  Sliders,
  BarChart3,
  Globe,
  Settings2,
  Clock,
  RefreshCcw,
  Mail,
  ChevronRight,
  ShieldX,
  Layers,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Introduction', icon: Cookie },
  { id: 'what-are-cookies', title: '2. What Are Cookies?', icon: ShieldCheck },
  { id: 'how-we-use', title: '3. How We Use Cookies', icon: Sliders },
  { id: 'types-of-cookies', title: '4. Types of Cookies', icon: Layers },
  { id: 'managing-cookies', title: '5. Managing Cookies', icon: Settings2 },
  { id: 'do-not-track', title: '6. Do Not Track Signals', icon: ShieldX },
  { id: 'retention-changes', title: '7. Retention & Policy Updates', icon: Clock },
  { id: 'non-use', title: '8. What We Do NOT Use Cookies For', icon: Lock },
  { id: 'contact-us', title: '9. Privacy Contacts', icon: Mail },
//   { id: 'future-cookie-tools', title: '10. Future Preference Center', icon: Sparkles },
];

const nonUsePoints = [
  'Selling personal or professional data to third-party data brokers.',
  'Publicly tracking or displaying your professional activity across the web.',
  'Notifying current or prospective employers about your platform usage.',
  'Sharing behavioral browsing history with unrelated advertising networks.',
];

const quickSummaryTable = [
  { type: 'Essential Cookies', purpose: 'Core platform security, session integrity, and system stability.', icon: Lock },
  { type: 'Authentication Cookies', purpose: 'Login state validation, multi-page security, and session maintenance.', icon: ShieldCheck },
  { type: 'Preference Cookies', purpose: 'UI configurations, display density, and saved workspace preferences.', icon: Sliders },
  { type: 'Analytics Cookies', purpose: 'Aggregated platform usage metrics, feature performance, and bug diagnosis.', icon: BarChart3 },
];

/* ========================================================================
  FUTURE COOKIE PREFERENCE CENTER DATA STRUCTURE
  ========================================================================
  Uncomment/Use these items when ready to implement dynamic consent controls.

  const futurePreferenceControls = [
    {
      id: 'essential',
      title: 'Strictly Necessary Cookies',
      desc: 'Required for authentication, system load balancing, and secure platform access. Cannot be turned off.',
      alwaysActive: true,
    },
    {
      id: 'functional',
      title: 'Functional & Preference Cookies',
      desc: 'Remembers custom UI layouts, theme preferences, and localized workspace settings.',
      alwaysActive: false,
    },
    {
      id: 'analytics',
      title: 'Performance & Aggregated Analytics',
      desc: 'Allows us to measure feature engagement and site speed using anonymized usage statistics.',
      alwaysActive: false,
    },
    {
      id: 'third-party',
      title: 'Third-Party Integration Cookies',
      desc: 'Enables embedded media, interactive support widgets, and external security verification.',
      alwaysActive: false,
    },
  ];
*/

export const CookiePolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                PRIVACY & TRANSPARENCY
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Cookie Policy
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Cookie className="w-4 h-4 text-cyan-400" />
            <span>Privacy-First Cookie Architecture</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Navigation
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Overview */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Cookie className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Introduction</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              This Cookie Policy explains how Nexgent uses cookies and similar storage technologies when you access our platform, application dashboards, and related recruitment services.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Cookies enable us to deliver a reliable, secure, and personalized interface while maintaining core system functionality. By continuing to use Nexgent, you acknowledge the use of cookies as described in this policy, subject to your browser preference configurations.
            </p>
          </section>

          {/* Section 2: What Are Cookies? */}
          <section id="what-are-cookies" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. What Are Cookies?</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Cookies are small text data files placed on your browser or device when you visit an online portal or web service.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Key Data Properties:</div>
              <ul className="text-slate-400 space-y-1.5 list-disc pl-4">
                <li>Help platforms recognize your session, authenticated credentials, and UI settings over time.</li>
                <li>Do not typically store directly identifiable personal details, but may associate session tokens with your account.</li>
                <li>Can be session-based (temporary) or persistent (remaining until expiry or manual browser clearance).</li>
              </ul>
            </div>
          </section>

          {/* Section 3: How Nexgent Uses Cookies */}
          <section id="how-we-use" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Sliders className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. How Nexgent Uses Cookies</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent uses cookies exclusively to support application reliability, security, and user experience:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Security & Session Health</div>
                <div className="text-slate-400">Maintaining active, secure recruiter and candidate session tokens across page loads.</div>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">User Experience & Customization</div>
                <div className="text-slate-400">Saving filter preferences, table density choices, and dashboard display options.</div>
              </div>
            </div>
          </section>

          {/* Section 4: Types of Cookies We Use */}
          <section id="types-of-cookies" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Layers className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Types of Cookies We Use</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              We classify our cookie implementation into four functional categories:
            </p>

            <div className="space-y-3 text-xs">
              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold text-sm">Essential Cookies</div>
                <p className="text-slate-400 leading-relaxed">
                  Strictly necessary for platform operation, routing, and access control. Without these cookies, authentication and core portal functions will fail.
                </p>
              </div>

              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold text-sm">Authentication Cookies</div>
                <p className="text-slate-400 leading-relaxed">
                  Validate identity upon login, prevent unauthorized access to candidate/recruiter dashboards, and protect session handshakes.
                </p>
              </div>

              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold text-sm">Preference Cookies</div>
                <p className="text-slate-400 leading-relaxed">
                  Store custom configuration selections (e.g., table layouts, candidate filter criteria, and interface density) so you do not have to reconfigure them every visit.
                </p>
              </div>

              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold text-sm">Analytics & Third-Party Performance Cookies</div>
                <p className="text-slate-400 leading-relaxed">
                  Help us analyze navigation patterns, page load speeds, and error logs using aggregated metrics to improve stability. Third-party infrastructure providers operates under strict contractual privacy terms.
                </p>
              </div>
            </div>

            {/* Quick Summary Table */}
            <div className="pt-4 space-y-2">
              <h3 className="text-xs font-mono text-cyan-400 uppercase font-semibold">Quick Cookie Reference</h3>
              <div className="overflow-x-auto border border-slate-800 rounded-xl">
                <table className="w-full text-left text-xs text-slate-400">
                  <thead className="bg-slate-950 text-slate-200 font-mono text-[11px] uppercase border-b border-slate-800">
                    <tr>
                      <th className="p-3">Cookie Category</th>
                      <th className="p-3">Primary Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 bg-slate-900/30">
                    {quickSummaryTable.map((row, idx) => {
                      const Icon = row.icon;
                      return (
                        <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-3 font-semibold text-white flex items-center gap-2">
                            <Icon className="w-4 h-4 text-cyan-400 shrink-0" />
                            <span>{row.type}</span>
                          </td>
                          <td className="p-3 text-slate-300">{row.purpose}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Section 5: Managing Cookies */}
          <section id="managing-cookies" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Settings2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Managing & Disabling Cookies</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Most modern web browsers allow you to manage cookie behavior through their privacy settings:
            </p>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-5">
              <li>View and inspect cookies stored on your device.</li>
              <li>Clear or delete existing stored cookie registers.</li>
              <li>Block third-party cookies or disable cookies for specific domains entirely.</li>
            </ul>
            <div className="p-3 bg-amber-950/30 border border-amber-900/40 text-amber-300 rounded-lg text-xs">
              Note: Blocking essential or authentication cookies will prevent you from signing in or utilizing key features across the Nexgent portal.
            </div>
          </section>

          {/* Section 6: Do Not Track Signals */}
          <section id="do-not-track" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldX className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Do Not Track (DNT) Signals</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Some web browsers offer "Do Not Track" headers or signals. Because uniform web standards for DNT compliance have not been broadly established across the industry, Nexgent may not alter its cookie processing in response to generic browser DNT signals.
            </p>
          </section>

          {/* Section 7: Retention & Policy Updates */}
          <section id="retention-changes" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Cookie Retention & Policy Changes</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Cookie retention lifespans vary by type:
            </p>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-5">
              <li><strong>Session Cookies:</strong> Cleared automatically once your web browser session closes.</li>
              <li><strong>Persistent Cookies:</strong> Remain stored for a predetermined period or until manually cleared.</li>
            </ul>
            <p className="text-xs text-slate-400 pt-1">
              We may update this policy periodically to reflect evolving technology or legal requirements. Material updates will be published here with a revised effective timestamp.
            </p>
          </section>

          {/* Section 8: What We Do NOT Use Cookies For */}
          <section id="non-use" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. What Nexgent Does NOT Use Cookies For</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              In line with our commitment to executive confidentiality, our cookies are strictly operational. Nexgent does NOT use cookies for:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs pt-1">
              {nonUsePoints.map((point, idx) => (
                <div key={idx} className="p-3 bg-slate-950/80 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span className="text-[11px] leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9: Privacy Contacts */}
          <section id="contact-us" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Mail className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Privacy & Support Contacts</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              If you have questions regarding our cookie practices or data privacy controls, reach out to our privacy officers:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">Privacy Concerns & Data Requests</div>
                <div className="text-cyan-400 font-mono font-semibold">privacy@nexgent.tech</div>
              </div>
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">General Technical Support</div>
                <div className="text-cyan-400 font-mono font-semibold">support@nexgent.tech</div>
              </div>
            </div>
            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Platform Security. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

          {/* Section 10: Additional Sections / Future Roadmap */}
          {/* <section id="future-cookie-tools" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">10. Future Interactive Preference Center</h2>
                <p className="text-xs text-slate-400">Granular consent management features reserved for platform expansion</p>
              </div>
            </div> */}

            {/* ========================================================================
              COMMENTED OUT SECTION BELOW
              Uncomment the block below when you are ready to display these live!
              ========================================================================
            */}

            {/* <div className="space-y-3 pt-2">
              <p className="text-xs text-slate-300">
                Customize your cookie preferences directly using our interactive toggle center:
              </p>
              <div className="space-y-2">
                {futurePreferenceControls.map((ctrl) => (
                  <div key={ctrl.id} className="p-4 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="text-white text-xs font-semibold">{ctrl.title}</div>
                      <p className="text-[11px] text-slate-400">{ctrl.desc}</p>
                    </div>
                    <div>
                      {ctrl.alwaysActive ? (
                        <span className="px-2.5 py-1 text-[10px] font-mono bg-cyan-950 text-cyan-400 border border-cyan-800/60 rounded-full">
                          ALWAYS ACTIVE
                        </span>
                      ) : (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            */}

            {/* Placeholder state while preference center is commented out */}
            {/* <div className="p-4 bg-slate-950/60 border border-slate-800/80 rounded-xl text-center space-y-2">
              <div className="inline-flex p-2 bg-slate-900 text-cyan-400 rounded-lg border border-slate-800">
                <Sparkles className="w-4 h-4" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed max-w-lg mx-auto">
                An interactive cookie consent banner and granular preferences modal (with individual category toggles for performance, functional, and integration cookies) are maintained in code and ready to activate.
              </p>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-slate-500 font-mono mt-4">
              © {new Date().getFullYear()} Motherson Infotech / Nexgent Cookie Governance. All rights reserved.
            </div>
          </section> */}

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default CookiePolicy;